import React from 'react';
import { useSidePanel } from '../../context/SidePanelContext';
import { useUser } from '../../context/UserContext';
import { postFeedback } from '../../api/ai';

// ── Sparkline SVG ──────────────────────────────────────────────────────────────
export function Spark({ data=[], w=70, h=26, color }) {
  if (!data||data.length<2) return <span style={{display:'inline-block',width:w,height:h}}/>;
  const mn=Math.min(...data),mx=Math.max(...data),rng=mx-mn||1;
  const pts=data.map((v,i)=>{
    const x=(i/(data.length-1))*(w-4)+2;
    const y=h-2-((v-mn)/rng)*(h-4);
    return `${x},${y}`;
  }).join(' ');
  const up=data[data.length-1]>=data[0];
  const c=color||(up?'var(--up-field-ink)':'var(--down-field-ink)');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{display:'block',flexShrink:0}}>
      <polyline points={pts} fill="none" stroke={c} strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Heat class ─────────────────────────────────────────────────────────────────
export function hc(v) {
  if (v>= 2.0) return 'cu4';
  if (v>= 1.0) return 'cu3';
  if (v>= 0.3) return 'cu2';
  if (v>= 0.05)return 'cu1';
  if (v<=-2.0) return 'cd4';
  if (v<=-1.0) return 'cd3';
  if (v<=-0.3) return 'cd2';
  if (v<=-0.05)return 'cd1';
  return 'cn0';
}

// ── Heat TD ───────────────────────────────────────────────────────────────────
export function HeatTD({ v }) {
  return (
    <td className={hc(v)} style={{textAlign:'right',padding:'4px 6px',fontFamily:'var(--font-data)',fontSize:11}}>
      {v>0?'+':''}{v?.toFixed(1)}%
    </td>
  );
}

// ── Ticker link (opens side panel) ────────────────────────────────────────────
export function Ticker({ name, sector='', style={} }) {
  const {open} = useSidePanel();
  return (
    <span
      className="tlink"
      style={style}
      onClick={() => open({name, sector})}
    >
      {name}
    </span>
  );
}

// ── Streak badge ──────────────────────────────────────────────────────────────
export function Streak({ v }) {
  const up = v?.startsWith('↑');
  return <span className={up?'streak-u':'streak-d'}>{v}</span>;
}

// ── Stars ─────────────────────────────────────────────────────────────────────
export function Stars({ n, max=5 }) {
  return (
    <span style={{letterSpacing:1}}>
      {Array.from({length:max},(_,i)=>(
        <span key={i} className={i<n?'star-on':'star-off'} style={{fontSize:11}}>★</span>
      ))}
    </span>
  );
}

// ── Seg control ───────────────────────────────────────────────────────────────
export function Seg({ opts, val, onChange }) {
  return (
    <div className="seg">
      {opts.map(o=>(
        <button key={o.v} className={`sbtn${val===o.v?' on':''}`} onClick={()=>onChange(o.v)}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ── Bias pill ─────────────────────────────────────────────────────────────────
export function BiasPill({ label }) {
  const m={
    'Bullish':  'bias-bull','Bearish':   'bias-bear',
    'Neutral':  'bias-neu', 'Low Fear':  'bias-info',
    'Positive': 'bias-bull','Long Build':'bias-bull',
    'Warning':  'bias-bear',
  };
  return <span className={m[label]||'bias-neu'} style={{display:'inline-flex',alignItems:'center',gap:4}}>
    <span style={{width:6,height:6,borderRadius:'50%',background:'currentColor',display:'inline-block'}}/>
    {label}
  </span>;
}

// ── Build-up badge ────────────────────────────────────────────────────────────
export function Buildup({ type }) {
  return <span className={`bu-${type}`}>{type}</span>;
}

// ── AI tag ────────────────────────────────────────────────────────────────────
export function AiTag({ type, tagKey }) {
  return <span className={`aitag tag-${(tagKey||type).toLowerCase().replace(/[^a-z]/g,'')}`}>{type}</span>;
}

// ── Change display ────────────────────────────────────────────────────────────
export function Chg({ chg, pct, size=11 }) {
  const pos = chg>=0;
  return (
    <span className={`mono ${pos?'up':'dn'}`} style={{fontSize:size}}>
      {pos?'+':''}{chg?.toFixed(2)} ({pos?'+':''}{pct?.toFixed(2)}%)
    </span>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
export function SL({ children, right }) {
  return (
    <div className="sl">
      <span>{children}</span>
      {right && <span>{right}</span>}
    </div>
  );
}

// ── Cond status ───────────────────────────────────────────────────────────────
export function CondStatus({ st }) {
  if (st==='ok')   return <span style={{color:'var(--up-field-ink)',fontWeight:700}}>✓</span>;
  if (st==='warn') return <span style={{color:'var(--amber)',fontWeight:700}}>⚠</span>;
  return <span style={{color:'var(--down-field-ink)',fontWeight:700}}>✕</span>;
}

// ── Sortable th ───────────────────────────────────────────────────────────────
export function Th({ col, sort, onSort, children, cls='' }) {
  const active = sort?.col===col;
  return (
    <th className={`${active?'srt':''} ${cls}`} onClick={()=>onSort(col)}>
      {children}<span className="sa">{active?(sort.dir>0?'↑':'↓'):'↕'}</span>
    </th>
  );
}

// ── AI Observation Feedback ───────────────────────────────────────────────────
// All icons grey until interacted with.
// Active thumb: gold/amber (#c8922a). Inactive thumb when other is active: grey faded.
// Comment icon: blue (var(--amber-ink)) when feedback saved, grey otherwise.
export function ObsFeedback({ obsId, tabSource = '', type = '' }) {
  const { isAdmin } = useUser();

  // All hooks declared unconditionally (React rules of hooks).
  // resolvedTab must also be computed before any early return to keep hook call order stable.
  const [rating, setRating] = React.useState(0); // -1, 0, +1
  const [open,   setOpen]   = React.useState(false);
  const [text,   setText]   = React.useState('');
  const [saved,  setSaved]  = React.useState('');
  const resolvedTab = tabSource || (obsId ? obsId.split('-')[0] : '');

  // POST to backend whenever rating changes
  React.useEffect(() => {
    if (!isAdmin) return;
    if (rating === 0) return;
    postFeedback({ obsId, rating, comment: saved, tabSource: resolvedTab, type })
      .catch(() => {}); // silent — don't interrupt UX on network error
  }, [rating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only admins and super_admins can rate AI observations
  if (!isAdmin) return null;

  const rate = v => setRating(r => r===v ? 0 : v);
  const save = () => {
    setSaved(text);
    setOpen(false);
    // POST with updated comment (rating may already be set)
    postFeedback({ obsId, rating, comment: text, tabSource: resolvedTab, type })
      .catch(() => {});
  };
  const hasFeedback = saved.length > 0;

  const GOLD = '#c8922a';
  const GREY = 'var(--ink-3)';

  return (
    <div style={{display:'inline-flex',flexDirection:'column',gap:3,alignItems:'flex-end'}}>
      <div style={{display:'inline-flex',alignItems:'center',gap:2}}>
        {/* Thumbs up — gold when active, grey otherwise, faded when other is active */}
        <button onClick={()=>rate(1)} title="Useful"
          style={{background:'none',border:'none',cursor:'pointer',padding:'1px 3px',
            fontSize:11,lineHeight:1,
            filter: rating===1 ? 'none' : rating===-1 ? 'grayscale(1) opacity(0.35)' : 'grayscale(1) opacity(0.55)',
            color: rating===1 ? GOLD : GREY,
            transition:'filter .15s, color .15s'}}>
          👍
        </button>
        {/* Thumbs down — gold when active, grey otherwise */}
        <button onClick={()=>rate(-1)} title="Not useful"
          style={{background:'none',border:'none',cursor:'pointer',padding:'1px 3px',
            fontSize:11,lineHeight:1,
            filter: rating===-1 ? 'none' : rating===1 ? 'grayscale(1) opacity(0.35)' : 'grayscale(1) opacity(0.55)',
            color: rating===-1 ? GOLD : GREY,
            transition:'filter .15s, color .15s'}}>
          👎
        </button>
        {/* Comment — blue when saved, grey otherwise */}
        <button onClick={()=>setOpen(o=>!o)} title={hasFeedback?'Edit note':'Add note'}
          style={{background:'none',border:'none',cursor:'pointer',padding:'1px 3px',
            fontSize:11,lineHeight:1,
            color: hasFeedback ? 'var(--amber-ink)' : GREY,
            opacity: hasFeedback ? 1 : 0.55,
            transition:'color .15s, opacity .15s'}}>
          ✎
        </button>
      </div>

      {open && (
        <div style={{display:'flex',flexDirection:'column',gap:4,background:'var(--surface-2)',
          border:'.5px solid var(--hair-strong)',borderRadius:5,padding:'6px 8px',minWidth:220}}>
          {saved && (
            <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic',marginBottom:2}}>
              Saved: {saved}
            </div>
          )}
          <textarea value={text} onChange={e=>setText(e.target.value)} rows={3}
            placeholder="What did this miss? What should it focus on?"
            style={{fontSize:11,padding:'4px 6px',border:'.5px solid var(--hair-strong)',borderRadius:3,
              background:'var(--surface)',color:'var(--ink)',resize:'vertical',outline:'none',
              fontFamily:'inherit',lineHeight:1.5}}/>
          <div style={{display:'flex',gap:5,justifyContent:'flex-end'}}>
            <button onClick={()=>setOpen(false)}
              style={{fontSize:11,padding:'2px 8px',borderRadius:3,border:'.5px solid var(--hair-strong)',
                color:'var(--ink-3)',background:'transparent',cursor:'pointer'}}>Cancel</button>
            <button onClick={save}
              style={{fontSize:11,padding:'2px 8px',borderRadius:3,border:'none',
                background:'var(--amber-ink)',color:'#fff',cursor:'pointer',fontWeight:600}}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Custom Select (replaces native <select> to avoid OS-styled dropdown) ─────
export function Select({ value, onChange, children, className = 'inp', style, disabled: selectDisabled, title }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Parse <option> and <optgroup> children into flat array
  const options = React.useMemo(() => {
    const arr = [];
    const parseOption = (child, grouped) => {
      if (!child || child.type !== 'option') return;
      const v = child.props.value !== undefined ? String(child.props.value) : String(child.props.children);
      arr.push({ value: v, label: child.props.children, disabled: !!child.props.disabled, grouped });
    };
    React.Children.forEach(children, child => {
      if (!child) return;
      if (child.type === 'optgroup') {
        arr.push({ group: true, label: child.props.label });
        React.Children.forEach(child.props.children, opt => parseOption(opt, true));
      } else {
        parseOption(child, false);
      }
    });
    return arr;
  }, [children]);

  const selectable = options.filter(o => !o.group);
  const current = selectable.find(o => o.value === String(value ?? ''));
  const displayLabel = current ? current.label : (value && value !== '' ? value : (selectable[0]?.label ?? ''));

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', ...style }}>
      <button
        type="button"
        className={className}
        disabled={selectDisabled}
        title={title}
        onClick={() => !selectDisabled && setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', width: '100%', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 0 }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayLabel}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0, lineHeight: 1 }}>▾</span>
      </button>
      {open && (
        <div className="sel-drop">
          {options.map((opt, i) => (
            opt.group
              ? <div key={i} style={{fontSize:10,color:'var(--ink-3)',padding:'6px 10px 2px',
                  fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',userSelect:'none'}}>
                  {opt.label}
                </div>
              : <div key={i}
                  className={`sel-opt${opt.disabled?' sel-dis':''}${opt.value===String(value??'')?' sel-on':''}`}
                  style={opt.grouped ? {paddingLeft:18} : undefined}
                  onClick={() => {
                    if (opt.disabled) return;
                    onChange({ target: { value: opt.value } });
                    setOpen(false);
                  }}>
                  {opt.label}
                </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-tabs ──────────────────────────────────────────────────────────────────
export function SubTabs({ tabs, val, onChange }) {
  return (
    <div className="subtabs" style={{overflowX:'clip'}}>
      {tabs.map(t=>(
        <button key={t.v} className={`stab${val===t.v?' on':''}`} onClick={()=>onChange(t.v)}>
          {t.l}
        </button>
      ))}
    </div>
  );
}
